import { getLanguageFromLocale } from '@/app/components/search/utils/misc.ts';

/**
 * Which TripAdvisor review languages the hotel detail shows (MPH-1202), from two CMS flags set per
 * domain/BP. When both are on, all languages wins:
 *
 *  - `PAGE` (both flags off): request TA in the page locale and list whatever comes back — reviews
 *    written in that language plus TA's machine translations into it. No section titles.
 *  - `PAGE_AND_ENGLISH` (`tripadvisor_reviews_fallback_english`): the same page-locale reviews under
 *    "your language", then, when they are fewer than TA_REVIEWS_TOTAL, a second request for TA's
 *    `primary` language (every review untranslated, in the language it was written in) fills the gap
 *    with English reviews not already listed.
 *  - `ALL` (`tripadvisor_reviews_fallback_all_languages`): same, but the gap is filled with reviews
 *    in any other language.
 */
export enum TRIPADVISOR_REVIEWS_LANGUAGES {
  PAGE = 'page',
  PAGE_AND_ENGLISH = 'page_en',
  ALL = 'all',
}

/** The hotel-reviews locale segment that asks TA for reviews in their original language. */
export const TRIPADVISOR_PRIMARY_LOCALE = 'primary';

/** TA returns the latest 10 reviews per request; the split modes never list more than that in total. */
export const TA_REVIEWS_TOTAL = 10;

const ENGLISH = 'en';

export const resolveTripadvisorReviewsLanguages = ({
  fallbackEnglish,
  fallbackAllLanguages,
}: {
  fallbackEnglish: boolean;
  fallbackAllLanguages: boolean;
}): TRIPADVISOR_REVIEWS_LANGUAGES => {
  if (fallbackAllLanguages) return TRIPADVISOR_REVIEWS_LANGUAGES.ALL;
  if (fallbackEnglish) return TRIPADVISOR_REVIEWS_LANGUAGES.PAGE_AND_ENGLISH;
  return TRIPADVISOR_REVIEWS_LANGUAGES.PAGE;
};

/** Whether the page-language reviews are followed by an other-languages section. */
export const isSplitByLanguage = (mode?: TRIPADVISOR_REVIEWS_LANGUAGES): boolean =>
  mode === TRIPADVISOR_REVIEWS_LANGUAGES.PAGE_AND_ENGLISH || mode === TRIPADVISOR_REVIEWS_LANGUAGES.ALL;

const toLanguage = (code?: string): string => getLanguageFromLocale(code ?? '').toLowerCase();

/**
 * Language a review was written in — its `primary` entry, the only one a `primary` request returns.
 * Falls back to the title for a review without text; '' when TA sent no language at all.
 */
export const getTripadvisorReviewLanguage = (review: TripadvisorReview): string => {
  const entries = [...review.text, ...review.title];
  return toLanguage((entries.find((entry) => entry.primary) ?? entries[0])?.language);
};

/**
 * Whether the other-languages section has room to fill and can get anything: in the split modes,
 * with fewer than TA_REVIEWS_TOTAL page-language reviews, and not English-only on an English page,
 * where every English review is already a page-language one.
 */
export const needsTripadvisorOtherLanguages = ({
  pageLanguageReviewCount,
  mode,
  locale,
}: {
  pageLanguageReviewCount: number;
  mode?: TRIPADVISOR_REVIEWS_LANGUAGES;
  locale: string;
}): boolean =>
  isSplitByLanguage(mode) &&
  pageLanguageReviewCount < TA_REVIEWS_TOTAL &&
  !(mode === TRIPADVISOR_REVIEWS_LANGUAGES.PAGE_AND_ENGLISH && toLanguage(locale) === ENGLISH);

/**
 * The untranslated reviews that fill the other-languages section: English or any language depending
 * on the mode, never one already listed in the page language (a translation keeps its original's id),
 * nor one written in the page language, and only as many as keep the total at TA_REVIEWS_TOTAL.
 */
export const getTripadvisorOtherLanguageReviews = ({
  pageLanguageReviews,
  primaryReviews,
  mode,
  locale,
}: {
  pageLanguageReviews: TripadvisorReview[];
  primaryReviews: TripadvisorReview[];
  mode: TRIPADVISOR_REVIEWS_LANGUAGES;
  locale: string;
}): TripadvisorReview[] => {
  if (!isSplitByLanguage(mode)) return [];
  const listedIds = new Set(pageLanguageReviews.map((review) => review.id));
  const pageLanguage = toLanguage(locale);
  return primaryReviews
    .filter((review) => {
      const language = getTripadvisorReviewLanguage(review);
      return (
        !listedIds.has(review.id) &&
        language !== '' &&
        language !== pageLanguage &&
        (mode === TRIPADVISOR_REVIEWS_LANGUAGES.ALL || language === ENGLISH)
      );
    })
    .slice(0, Math.max(TA_REVIEWS_TOTAL - pageLanguageReviews.length, 0));
};
